<template>
  <SoWidget class="prediction-chart__wrapper">
    <template #title>
      <div class="d-flex justify-space-between">
        <h6 class="font-weight-medium d-flex">
          <SoTooltip
            text="
              This graph shows the historic and predicted balance comparing if the
              customer continues on their current payment schedule or if they switch
              to the suggested payments. If the customer is on So flex or rolls on to
              So Flex, the rates will be subject to change within this period.
            "
            ><SoIcon
              icon="info"
              size="small"
              color="info"
              class="mr-2"
              style="position: relative; top: -2px"
          /></SoTooltip>
          Balance Prediction Graph
        </h6>
        <SoButton
          title="Export"
          prepend-icon="download"
          @click="downloadGraph"
        />
      </div>
    </template>

    <SoSkeleton v-if="predictedPayments.isFetching" class="mt-4" type="card" />
    <div v-else ref="payments-prediction-chart" class="prediction-chart">
      <SoLineChart
        :data-sets="dataSets"
        :labels="historicAndPredictedPaymentDates"
        :aspect-ratio="1.5"
        is-finance
      >
        <template #legend="{ toggles }">
          <div class="d-flex gap-1 justify-center flex-wrap">
            <div
              v-for="(data, index) in dataSets"
              :key="data.label"
              :label="data.label"
              class="d-flex align-center"
              @click="toggles[index] = !toggles[index]"
            >
              <template v-if="data.label">
                <div
                  :class="
                    data.label === historicPaymentsLabel
                      ? 'balance-key'
                      : 'dashed-key'
                  "
                  :style="
                    data.label === historicPaymentsLabel
                      ? historicPaymentsKeyColor
                      : `border-color:${data.lineColor}`
                  "
                  class="mr-1"
                ></div>
                <div
                  class="text-body-2"
                  :class="{ 'text-decoration-line-through': !toggles[index] }"
                >
                  {{ data.label }}
                </div>
              </template>
            </div>
          </div>
        </template>
      </SoLineChart>
    </div>
  </SoWidget>
</template>

<script lang="ts">
  import { defineComponent } from "vue"
  import { toPng } from "html-to-image"
  import { useThemeHelper } from "@/theme/themeHelper"
  import { useCurrentAccountStore } from "@/stores/currentAccount"
  import { getPredictedPayments } from "@/services-handlers/getPredictedPayments"
  import { getAccountMeterpointAgreements } from "@/services-handlers/getAccountMeterpointAgreements"
  import SoWidget from "@/components/SoWidget.vue"
  import SoSkeleton from "@/components/SoSkeleton.vue"
  import SoLineChart, { DataSet } from "@/components/charts/SoLineChart.vue"
  import SoButton from "@/components/formControls/SoButton.vue"
  import SoTooltip from "@/components/SoTooltip.vue"
  import SoIcon from "@/components/SoIcon.vue"
  import { Dayjs } from "dayjs"

  export default defineComponent({
    components: {
      SoSkeleton,
      SoWidget,
      SoLineChart,
      SoButton,
      SoTooltip,
      SoIcon,
    },
    data() {
      return {
        theme: useThemeHelper(),
        current: useCurrentAccountStore(),
        transactions: useCurrentAccountStore().transactions,
        historicPaymentsLabel: "Historic",
        historicPaymentsFactor: 3,
        interpolationFactor: 12,
        getAccountMeterpointAgreements,
      }
    },
    computed: {
      historicPayments() {
        return this.transactions.data
          .filter((transaction) => transaction.isAccepted)
          .sort((a, b) => (a.acceptedDttm?.isAfter(b.acceptedDttm) ? 1 : -1))
          .slice(-this.historicPaymentsFactor)
      },
      historicAndPredictedPaymentDates() {
        return this.historicPayments
          .map((transaction) => transaction.acceptedDttm)
          .concat(
            this.predictedPayments.data
              .map((transaction) => transaction.fromDate)
              .slice(1),
          ) as Dayjs[]
      },
      predictedPayments() {
        return getPredictedPayments(this.interpolationFactor)
      },
      predictedPaymentsWithSuggestedAmount() {
        return getPredictedPayments(this.interpolationFactor, true)
      },
      showPredictedPayments() {
        return (
          !!this.predictedPayments.data.length &&
          [...this.predictedPayments.data].slice(1).every((o) => o.balance)
        )
      },
      historicPaymentsKeyColor() {
        const hasPositiveData = this.historicPayments.some(
          (data) => -data.accountBalance > 0,
        )
        const hasNegativeData = this.historicPayments.some(
          (data) => -data.accountBalance < 0,
        )
        if (hasPositiveData && hasNegativeData) {
          return `background-image:linear-gradient(
              90deg,
              rgb(var(--v-theme-normal)) 0%,
              rgb(var(--v-theme-caution)) 50%,
              rgb(var(--v-theme-critical)) 100%
            )`
        } else {
          return hasPositiveData
            ? "background-color: rgb(var(--v-theme-normal))"
            : "background-color: rgb(var(--v-theme-critical))"
        }
      },
      dataSets(): DataSet[] {
        return [
          {
            label: this.historicPaymentsLabel,
            lineColor: (context) => {
              if (!context.chart.chartArea) return
              return this.getGradient(context.chart)
            },
            backgroundColor: (context) => {
              if (!context.chart.chartArea) return
              return this.getGradient(context.chart, "33")
            },
            data: this.historicPayments.map(
              (transaction) => -transaction.accountBalance,
            ),
            fill: true,
          },
          this.showPredictedPayments
            ? {
                label: "Predicted with current payments",
                lineColor: this.theme.colors.caution,
                lineType: "dashed",
                data: [
                  ...Array(this.historicPaymentsFactor - 1).fill(null),
                  ...this.predictedPayments.data.map(
                    (payment) => payment.balance,
                  ),
                ],
              }
            : ({} as DataSet),
          this.predictedPaymentsWithSuggestedAmount.data.length
            ? {
                label: "Predicted with suggested payments",
                lineColor: this.theme.colors.neutral,
                lineType: "dashed",
                data: [
                  ...Array(this.historicPaymentsFactor - 1).fill(null),
                  ...this.predictedPaymentsWithSuggestedAmount.data.map(
                    (payment) => payment.balance,
                  ),
                ],
              }
            : ({} as DataSet),
        ]
      },
    },
    methods: {
      downloadGraph() {
        const chart = this.$refs["payments-prediction-chart"] as HTMLElement
        toPng(chart, { skipFonts: true }).then((dataUrl) => {
          const a = document.createElement("a")
          a.href = dataUrl
          a.download = `payment_predictions_graph_${this.current.accountNumber}.png`
          a.click()
        })
      },
      getGradient(chart, opacity = "") {
        const above = chart.scales.y.getPixelForValue(100)
        const below = chart.scales.y.getPixelForValue(-100)

        const gradient = chart.ctx.createLinearGradient(0, above, 0, below)
        gradient.addColorStop(0, this.theme.colors.normal + opacity)
        gradient.addColorStop(0.5, this.theme.colors.normal + opacity)
        gradient.addColorStop(0.5, this.theme.colors.caution + opacity)
        gradient.addColorStop(1, this.theme.colors.critical + opacity)

        return gradient
      },
    },
  })
</script>

<style scoped>
  .prediction-chart__wrapper {
    display: grid;
  }

  .prediction-chart {
    padding-top: 8px;
    padding-bottom: 42px;
    background-color: rgb(var(--v-theme-surface));
  }

  .balance-key {
    display: inline-block;
    height: 2px;
    width: 16px;
    margin-top: 2px;
  }

  .dashed-key {
    display: inline-block;
    border: none;
    border-top: 2px dashed;
    height: 4px;
    width: 16px;
    margin-top: 4px;
  }
</style>
